import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

const palette = {
  primary: '#1F3A5F',
  secondary: '#2C8C8C',
  accent: '#D6A75B',
  bg: '#F6F4EF',
  bgDark: '#0F1E2E',
  bgCard: '#FFFFFF',
  textDark: '#1a1a2e',
  textMuted: '#5a6872',
  textLight: '#E0E0E0',
  border: '#e8e4de',
  sectionAlt: '#F0EDE6',
};

const sectionImages = [
  '/images/landing/feature-quick-setup.webp',
  '/images/landing/feature-shopee.webp',
  '/images/landing/feature-cvs-pickup.webp',
  '/images/landing/feature-payment.webp',
  '/images/landing/feature-analytics.webp',
  '/images/landing/feature-responsive.webp',
];

function Features() {
  const { t } = useTranslation('landing');
  const navigate = useNavigate();

  const sections = sectionImages.map((image, idx) => {
    const n = idx + 1;
    return {
      title: t(`features_page.section_${n}_title`),
      subtitle: t(`features_page.section_${n}_subtitle`),
      image,
      points: t(`features_page.section_${n}_points`, { returnObjects: true }) || [],
      desc: t(`features_page.section_${n}_desc`),
      cardNote: t(`features_page.section_${n}_note`),
    };
  });

  return (
    <Box sx={{ bgcolor: palette.bg, minHeight: '100vh' }}>
      {/* Navbar */}
      <Box
        sx={{
          bgcolor: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${palette.border}`,
          py: 1.5,
          position: 'sticky',
          top: 0,
          zIndex: 1100,
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Button
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/landing')}
                sx={{ color: palette.textMuted, fontWeight: 900 }}
              >
                {t('landing_footer.back_home')}
              </Button>
            </Stack>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate('/apply?plan=free')}
              sx={{
                bgcolor: palette.accent,
                color: palette.bgDark,
                fontWeight: 950,
                borderRadius: 2,
                px: 2.5,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#c4963e' },
              }}
            >
              {t('landing_nav.free_trial')}
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Hero */}
      <Box sx={{ bgcolor: palette.bgDark, color: 'white', py: { xs: 7, md: 9 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Chip
            label={t('features_page.chip')}
            size="small"
            sx={{
              mb: 2,
              bgcolor: 'rgba(214,167,91,0.14)',
              color: palette.accent,
              fontWeight: 900,
              border: '1px solid rgba(214,167,91,0.28)',
            }}
          />
          <Typography variant="h2" sx={{ fontWeight: 950, fontSize: { xs: '2rem', md: '2.8rem' }, mb: 2, letterSpacing: '-0.03em' }}>
            {t('features_page.hero_title_1')}
            <br />
            {t('features_page.hero_title_2')}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.85, fontWeight: 400, color: palette.textLight, lineHeight: 1.7 }}>
            {t('features_page.hero_subtitle')}
          </Typography>
        </Container>
      </Box>

      {/* Sections */}
      {sections.map((s, idx) => (
        <Box key={s.title} sx={{ bgcolor: idx % 2 === 0 ? palette.bg : palette.sectionAlt, py: { xs: 7, md: 9 } }}>
          <Container maxWidth="lg">
            <Grid container spacing={5} alignItems="center" direction={idx % 2 === 0 ? 'row' : 'row-reverse'}>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: 950, color: palette.textDark, fontSize: { xs: '1.8rem', md: '2.35rem' }, mb: 1 }}>
                  {s.title}
                </Typography>
                <Typography sx={{ color: palette.secondary, fontWeight: 900, mb: 2 }}>
                  {s.subtitle}
                </Typography>
                <Typography sx={{ color: palette.textMuted, lineHeight: 1.85, mb: 3 }}>
                  {s.desc}
                </Typography>

                <Grid container spacing={1.2}>
                  {s.points.map((p) => (
                    <Grid item xs={12} sm={6} key={p}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CheckIcon sx={{ fontSize: 18, color: palette.secondary }} />
                        <Typography variant="body2" sx={{ color: palette.textDark }}>
                          {p}
                        </Typography>
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    bgcolor: palette.bgCard,
                    border: `1px solid ${palette.border}`,
                    overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.06)',
                  }}
                >
                  <Box
                    component="img"
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    sx={{ width: '100%', height: 'auto', display: 'block' }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography sx={{ color: palette.textMuted, lineHeight: 1.7 }}>
                      {s.cardNote}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </Box>
      ))}

      {/* CTA */}
      <Box sx={{ bgcolor: palette.bgDark, color: 'white', py: { xs: 7, md: 9 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 950, mb: 1 }}>
            {t('features_page.cta_title')}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.85, fontWeight: 400, mb: 4, color: palette.textLight }}>
            {t('features_page.cta_subtitle')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/apply?plan=free')}
              endIcon={<ArrowForwardIcon />}
              sx={{
                bgcolor: palette.accent,
                color: palette.bgDark,
                px: 5,
                py: 1.5,
                fontSize: '1.05rem',
                fontWeight: 950,
                borderRadius: 2,
                '&:hover': { bgcolor: '#c4963e' },
              }}
            >
              {t('features_page.cta_button')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/landing')}
              sx={{
                borderColor: 'rgba(255,255,255,0.28)',
                color: 'white',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 900,
                '&:hover': { borderColor: 'rgba(255,255,255,0.48)', bgcolor: 'rgba(255,255,255,0.06)' },
              }}
            >
              {t('landing_footer.back_home')}
            </Button>
          </Stack>
          <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.08)' }} />
          <Typography variant="body2" sx={{ opacity: 0.35 }}>
            Copyright © 2026 URStore. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

export default Features;

